/**
 * StepperBar
 *
 * DS2026 progress indicator for the contact-import wizard (Figma "Steps / V4").
 * - active + completed step → brand-filled circle with a white inner dot
 * - upcoming step           → white circle with a gray (line) border
 * - the connecting line fills bright-green for each completed segment
 * Each step shows a "Step N" caption above a bold label, centered below the
 * circle. Completed steps stay clickable so the user can jump back.
 */
import { Fragment } from 'react'
import { cn } from 'components/UI/cn'
import { WizardStep } from '../wizardState'

export interface StepperStep {
  key: WizardStep
  label: string
}

export const STEPPER_STEPS: StepperStep[] = [
  { key: 'upload', label: 'Upload' },
  { key: 'map', label: 'Map Columns' },
  { key: 'validate', label: 'Validate' },
  { key: 'review', label: 'Review & Import' },
]

interface StepperBarProps {
  currentStep: WizardStep
  /** Optional handler invoked when a completed step is clicked. */
  onStepClick?: (step: WizardStep) => void
}

export function StepperBar({ currentStep, onStepClick }: StepperBarProps) {
  const currentIdx = STEPPER_STEPS.findIndex((s) => s.key === currentStep)

  return (
    // pb reserves room for the absolutely-positioned labels below each circle;
    // px keeps the first/last labels (centered w-32 boxes that extend ~64px past
    // their circle) from clipping at the card edges.
    <div className="relative flex w-full items-center px-14 pb-12">
      {STEPPER_STEPS.map((step, idx) => {
        const isComplete = idx < currentIdx
        const isActive = idx === currentIdx
        const isFilled = isComplete || isActive
        const isClickable = isComplete && onStepClick != null

        return (
          <Fragment key={step.key}>
            {/* A single button covers both the circle and the label, so each
                step is one tab stop (not two). The label is absolutely
                positioned so it doesn't add height to the circle row, keeping
                the connecting line aligned to the circle centers — clicks on it
                still trigger this button since it's a DOM child. */}
            <div className="relative flex shrink-0 flex-col items-center">
              <button
                type="button"
                disabled={!isClickable}
                onClick={isClickable ? () => onStepClick(step.key) : undefined}
                aria-current={isActive ? 'step' : undefined}
                className={cn(
                  'flex flex-col items-center',
                  isClickable ? 'cursor-pointer' : 'cursor-default'
                )}
              >
                <span
                  className={cn(
                    'relative z-10 flex size-8 items-center justify-center rounded-full border transition-all duration-300',
                    isFilled
                      ? 'border-brand-active bg-brand-active'
                      : 'border-ds2026-border-tertiary bg-surface'
                  )}
                >
                  {isFilled && (
                    <span className="size-3 rounded-full bg-white" />
                  )}
                </span>

                <span className="absolute top-10 flex w-32 flex-col items-center text-center">
                  <span className="text-xs font-medium text-fg-secondary">
                    Step {idx + 1}
                  </span>
                  <span
                    className={cn(
                      'text-sm font-semibold transition-colors duration-300',
                      // DS: filled (active/completed) steps use the secondary
                      // gray; upcoming steps use the darker title color.
                      isFilled ? 'text-fg-secondary' : 'text-fg'
                    )}
                  >
                    {step.label}
                  </span>
                </span>
              </button>
            </div>

            {/* Connecting line (fills for completed segments) */}
            {idx < STEPPER_STEPS.length - 1 && (
              <div className="relative mx-2 h-1 flex-auto">
                <div className="absolute inset-0 rounded-full bg-line" />
                <div
                  className="absolute inset-y-0 left-0 rounded-full bg-brand-bright transition-all duration-300"
                  style={{ width: isComplete ? '100%' : '0%' }}
                />
              </div>
            )}
          </Fragment>
        )
      })}
    </div>
  )
}
